import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import './invoca.interfaces';

interface InvocaWindow extends Window {
    InvocaTagId?: string;
    Invoca?: { PNAPI?: { run?: () => void; currentPageSettings?: unknown } };
}

const GA_COOKIE_MAX_ATTEMPTS = 30;
const RUN_MAX_ATTEMPTS = 50;
const POLL_INTERVAL_MS = 200;
const G_CID_COOKIE_TTL_MS = 30 * 60 * 1000;

/**
 * Boots Invoca (call tracking) directly from app code instead of via GTM, removing the
 * ~870ms GTM chain (the shared module's 200ms delay, plus gtm.js load) that Invoca's own
 * one-shot DOM scan otherwise races against. See ASR-418/ASR-431.
 *
 * Ports the three GTM tags it replaces 1:1:
 * 1. Read GA's `_ga` cookie and mirror the client id into the `g_cid` cookie Invoca reads.
 * 2. Inject the Invoca tag script.
 * 3. Poll until Invoca is ready and `g_cid` is set, then call `Invoca.PNAPI.run()` once.
 *
 * `InvocaRescanService` (also injected at bootstrap) covers everything that happens after
 * this point - DOM changes that need a re-swap once Invoca is already running.
 */
@Injectable({
    providedIn: 'root'
})
export class InvocaBootstrapService {
    private readonly app = inject(App);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly timeoutService = inject(TimeoutService);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        const config = this.app.dfConfig.invoca;
        if (!this.app.isPlatformBrowser || !config?.tagId || !config?.scriptSrc) {
            return;
        }

        this.setGCidCookie();
        this.loadInvocaScript(config.tagId, config.scriptSrc);
        this.logDebug('[Invoca Run tag fired]', new Date().toISOString());
        this.runWhenReady();
    }

    private getGaClientId(): string | null {
        const match = this.document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
        if (!match) {
            return null;
        }
        const parts = match[1].split('.');
        return parts.length < 2 ? null : parts.slice(-2).join('.');
    }

    private setGCidCookie(attempt = 0): void {
        const cid = this.getGaClientId();
        if (cid) {
            const expires = new Date(Date.now() + G_CID_COOKIE_TTL_MS).toUTCString();
            this.document.cookie = `g_cid=${encodeURIComponent(cid)};expires=${expires};path=/`;
            return;
        }
        if (attempt < GA_COOKIE_MAX_ATTEMPTS) {
            const timeout = this.timeoutService.setTimeout(() => this.setGCidCookie(attempt + 1), POLL_INTERVAL_MS);
            this.destroyRef.onDestroy(() => clearTimeout(timeout));
        }
    }

    private loadInvocaScript(tagId: string, scriptSrc: string): void {
        const invocaWindow = this.app.window as unknown as InvocaWindow;
        invocaWindow.InvocaTagId = tagId;

        const script = this.document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `${this.document.location.protocol === 'https:' ? 'https://' : 'http://'}${scriptSrc}`;

        const firstScript = this.document.getElementsByTagName('script')[0];
        firstScript?.parentNode?.insertBefore(script, firstScript);
    }

    private hasGCidCookie(): boolean {
        return this.document.cookie.includes('g_cid=');
    }

    private isInvocaReady(): boolean {
        const invocaWindow = this.app.window as unknown as InvocaWindow;
        return !!(invocaWindow.Invoca?.PNAPI?.currentPageSettings && typeof invocaWindow.Invoca.PNAPI.run === 'function');
    }

    private tryRun(): boolean {
        try {
            (this.app.window as unknown as InvocaWindow).Invoca?.PNAPI?.run?.();
            return true;
        } catch {
            // not ready yet despite the checks above - caller keeps polling
            return false;
        }
    }

    private logDebug(...args: unknown[]): void {
        if (this.document.location.search.includes('gtm_debug')) {
            // eslint-disable-next-line no-console
            console.log(...args);
        }
    }

    private runWhenReady(attempt = 0): void {
        if (this.isInvocaReady() && this.hasGCidCookie() && this.tryRun()) {
            return;
        }

        if (attempt < RUN_MAX_ATTEMPTS) {
            const timeout = this.timeoutService.setTimeout(() => this.runWhenReady(attempt + 1), POLL_INTERVAL_MS);
            this.destroyRef.onDestroy(() => clearTimeout(timeout));
            return;
        }

        // Mirrors the original tag: these two lines always log, regardless of `gtm_debug`.
        if (this.isInvocaReady() && this.tryRun()) {
            // eslint-disable-next-line no-console
            console.warn(`Invoca run() fired (fallback) after ${RUN_MAX_ATTEMPTS * POLL_INTERVAL_MS}ms`);
        } else {
            // eslint-disable-next-line no-console
            console.error(`Invoca settings not initialized after ${RUN_MAX_ATTEMPTS * POLL_INTERVAL_MS}ms`);
        }
    }
}
